// The CARD of the Map mode: the tune map as text (music/tuneMapCard.ts), one
// line a cell in four aligned columns — interval chip, device, shape, chords
// (the position from home and the device first, then the shape that sits
// there; a line with no device leaves its column empty so the shapes line
// up) — under section headings in chart order. It is what the Map surface shows
// by default and what prints: the grid (TuneMapView) says far less per bar
// than a chart and left most of a page empty. The detail level maps onto the
// columns: the chords shown, faded until a line is tapped, or no chords
// column at all; the quiz (another key) hides the chords until tapped. An
// interval chip opens its fretboard hint as on the grid. The line the band is
// playing wears the playing wash; the loop and the start cursor stay the
// grid's.

import { useEffect, useMemo, useState } from 'react';
import type { IRealChart } from '@/music/ireal/types';
import { DEVICE_LABELS, type TuneMap } from '@/music/tuneMap';
import { tuneMapCard, type TuneMapCardLine, type TuneMapCardMark } from '@/music/tuneMapCard';
import type { TuneMapDetail } from '@/state/store';
import { ChordSymbol, CodaGlyph, SegnoGlyph } from './chartChrome';
import { cellHint } from './tuneMapHint';

interface TuneMapCardProps {
  chart: IRealChart;
  map: TuneMap;
  detail: TuneMapDetail;
  /** The chart is transposed and the chords are the answer: hidden until tapped. */
  playingMeasureId?: string | null;
}

/** The mark kinds that open a section and sit beside its letter. */
const LEADING_MARKS = new Set<TuneMapCardMark['kind']>(['repeat-open', 'segno', 'coda']);

const lineId = (line: TuneMapCardLine) => `${line.cell}@${line.bars[0]}`;

/** A repeat sign: the thick and thin bars with the two dots, as an inline SVG
 *  in the text colour, so it prints and renders alike everywhere; the repeat
 *  back is the same drawing mirrored. */
function RepeatSign({ side }: { side: 'open' | 'close' }) {
  return (
    <svg
      className={`mark-repeat-sign mark-repeat-${side}`}
      viewBox="0 0 12 16"
      width="12"
      height="16"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="0" y="0" width="3" height="16" />
      <rect x="4.5" y="0" width="1.2" height="16" />
      <circle cx="9.6" cy="4.5" r="1.5" />
      <circle cx="9.6" cy="11.5" r="1.5" />
    </svg>
  );
}

/** The chart's structure as a row of its own between the lines: a repeat
 *  sign, a segno or coda sign, a directive's words, or a short volta bracket
 *  for an ending. Signs stand alone; nothing runs across the page. */
function Mark({ mark, inline = false }: { mark: TuneMapCardMark; inline?: boolean }) {
  const name = {
    'repeat-open': 'repeat',
    'repeat-close': 'repeat back',
    ending: `ending ${mark.text}`,
    segno: 'segno',
    coda: 'coda',
    directive: mark.text,
  }[mark.kind];
  return (
    <div
      className={`map-card-mark mark-${mark.kind}${inline ? ' map-card-mark-inline' : ''}`}
      role="separator"
      aria-label={name}
      data-kind={mark.kind}
      data-testid="map-card-mark"
    >
      {mark.kind === 'repeat-open' && <RepeatSign side="open" />}
      {mark.kind === 'repeat-close' && <RepeatSign side="close" />}
      {mark.kind === 'ending' && <span className="mark-ending-label">{mark.text}</span>}
      {mark.kind === 'segno' && (
        <span className="mark-sign">
          <SegnoGlyph />
        </span>
      )}
      {mark.kind === 'coda' && (
        <span className="mark-sign">
          <CodaGlyph />
        </span>
      )}
      {mark.kind === 'directive' && <span className="mark-directive">{mark.text}</span>}
    </div>
  );
}

export function TuneMapCard({ chart, map, detail, playingMeasureId }: TuneMapCardProps) {
  const card = useMemo(() => tuneMapCard(chart, map), [chart, map]);
  const [revealed, setRevealed] = useState<ReadonlySet<string>>(() => new Set());
  const [openHint, setOpenHint] = useState<string | null>(null);
  // A new map (another tune, a different level) starts
  // with every chord hidden again and no hint open.
  useEffect(() => {
    setRevealed(new Set());
    setOpenHint(null);
  }, [map, detail]);
  // Dimmed and hidden chords wait for a tap on their line. Hidden chords keep
  // their place on the line and simply vanish.
  const interactive = detail !== 'shown';
  const blank = detail === 'hidden';
  const playingBar = useMemo(() => {
    if (!playingMeasureId) return -1;
    return chart.measures.findIndex((m) => m.id === playingMeasureId);
  }, [chart, playingMeasureId]);

  const renderLine = (line: TuneMapCardLine) => {
    const id = lineId(line);
    const cell = map.cells[line.cell];
    const hint = cellHint(map, cell);
    const open = openHint === id;
    const faded = interactive && !revealed.has(id);
    const playing = playingBar >= line.bars[0] && playingBar <= line.bars[1];
    const chords = line.chords.map((bar, b) => (
      <span key={b} className="map-card-bar">
        {b > 0 && (
          <span className="map-bar-sep" aria-hidden="true">
            |
          </span>
        )}
        {bar.map((c) => (
          <span
            key={c.id}
            className={`map-chord${faded ? ' hidden' : ''}${faded && blank ? ' blank' : ''}`}
            data-testid="map-chord"
          >
            <ChordSymbol symbol={c.symbol} className="map-chord-symbol" />
          </span>
        ))}
      </span>
    ));
    return (
      <div
        key={id}
        className="map-card-line"
        data-testid="map-card-line"
        data-approach={String(line.approach)}
        data-playing={playing || undefined}
      >
        <span className="map-card-chip">
          <button
            type="button"
            className="map-chip"
            title={hint}
            aria-label={hint}
            aria-expanded={open}
            data-testid="map-chip"
            onClick={() => setOpenHint(open ? null : id)}
          >
            {line.interval}
          </button>
          {open && (
            <span className="map-hint" role="note" data-testid="map-hint">
              {hint}
            </span>
          )}
        </span>
        <span className="map-card-tag">{line.name ? DEVICE_LABELS[line.name] : ''}</span>
        <span className="map-card-shape">{line.shape}</span>
        {interactive ? (
          <button
            type="button"
            className="map-card-chords map-reveal"
            aria-label={faded ? 'Reveal the chords' : 'Hide the chords'}
            data-faded={faded || undefined}
            data-blank={(faded && blank) || undefined}
            data-testid="map-card-chords"
            onClick={() =>
              setRevealed((old) => {
                const next = new Set(old);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              })
            }
          >
            {chords}
          </button>
        ) : (
          <span className="map-card-chords" data-testid="map-card-chords">
            {chords}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="map-card" data-detail={detail}>
      <div className="map-card-body">
        {card.sections.map((section, i) => {
          // The signs at a lettered section's start (a repeat opening, a
          // segno, a coda sign) sit beside the letter, not on rows of their own.
          let lead = 0;
          while (section.label && lead < section.rows.length) {
            const row = section.rows[lead];
            if (row.kind !== 'mark' || !LEADING_MARKS.has(row.mark.kind)) break;
            lead += 1;
          }
          const leading = section.rows.slice(0, lead);
          const rest = section.rows.slice(lead);
          return (
            <div key={i} className="map-card-section" data-testid="map-card-section">
              {section.label && (
                <div className="map-card-heading" data-testid="map-card-heading">
                  <span className="map-card-letter">{section.label}</span>
                  {leading.map(
                    (row, r) =>
                      row.kind === 'mark' && <Mark key={`lead-${r}`} mark={row.mark} inline />,
                  )}
                </div>
              )}
              {rest.map((row, r) =>
                row.kind === 'line' ? (
                  renderLine(row.line)
                ) : (
                  <Mark key={`mark-${r}`} mark={row.mark} />
                ),
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
